import * as React from 'react';
import { ServiceType } from '../domain/types';
import { type IconGlyph as IconGlyphOld } from '@sunwebgroup/ui-components';
import { Icon, type IconGlyph } from '@sunwebgroup/ui-components/uds';

export function ServiceIcon({ type, size }: { type: string; size: 'sm' | 'md' | 'lg' }) {
  const serviceIcon = getIconForService(type);
  if (!serviceIcon) {
    return null;
  }
  return <Icon glyph={serviceIcon} size={size} />;
}

/**
 * @deprecated Use `GetIconForService` method instead, which is the UDS replacement for this component.
 */
export const getOldIconForService = (service: string): IconGlyphOld | undefined => {
  const serviceIconMap: Record<string, IconGlyphOld> = {
    group: 'IconGroup',
    cutlery: 'IconCutlery',
    airplane: 'IconAirplane',
    suitcase: 'IconSuitcase',
    'bus-right': 'IconBusRight',
    skimaterial: 'IconSkiMaterial',
    'ski-material': 'IconSkiMaterial',
    skipass: 'IconSkiPass',
    'ski-pass': 'IconSkiPass',
    skilesson: 'IconSkier',
    'ski-lesson': 'IconSkier',
    bus: 'IconBusFront',
    flight: 'IconAirplane',
    accommodation: 'IconBed',
    building: 'IconBuilding',
    'shield-check': 'IconShieldWithDismiss',
    'luggage-check': 'IconLuggageCheck',
    parking: 'IconParking',
    earth: 'IconPlaneEarth',
    Accommodation: 'IconBed',
    Mealplan: 'IconCutlery',
    Flight: 'IconAirplane',
    Luggage: 'IconLuggage',
    'AUTOHUUR*': 'IconCar',
    'TRAVEL*': 'IconLuggageCheck',
    'CANCEL*': 'IconShieldWithDismiss',
    TRANSFER: 'IconBusRight',
    'VISA*': 'IconFile',
    REBOOKING: 'IconPlaneEarth',
    'BABY*': 'IconBaby',
    ACCOMMODATION: 'IconBed',
    'VERZORGING*': 'IconCutlery',
    'HALFBOARD*': 'IconCutlery',
    BREAKFAST: 'IconCutlery',
    BREAKFARRHINT_00: 'IconCutlery',
    DINNER: 'IconCutlery',
    REDEEM: 'IconOrder',
    'LUG_*': 'IconLuggage',
    'SKI*': 'IconSkiPass',
    'SKIPAS*': 'IconSkiPass',
    'SKIHUUR*': 'IconSkiMaterial',
    'SKILES*': 'IconSkier',
    'SWEATER*': 'IconSweater',
    'TSHIRT*': 'IconTShirt',
    DUTCHWEEK1: 'IconTicketFlight',
    DUTCHWEEK: 'IconTicketFlight',
    COMEDY: 'IconTicketGeneral',
    PARKEREN: 'IconParking',
    SHAREDBUS: 'IconBusRight',
    'CARRIAGESKI*': 'IconSuitcase',
    PARTY: 'IconTicketFlight',
    BUS: 'IconBusRight',
    GREENFEE: 'IconGolfClub',
    BRIDGE: 'IconTicketGeneral',
    'MEAL-VOUCHERS': 'IconCutlery',
    [ServiceType.CarRental]: 'IconCar',
    [ServiceType.CarRentalFiltering]: 'IconCar',
    [ServiceType.TravelInsurance]: 'IconLuggageCheck',
    [ServiceType.TravelInsurancePerBooking]: 'IconLuggageCheck',
    [ServiceType.CancellationInsurance]: 'IconShieldWithDismiss',
    [ServiceType.Transfer]: 'IconBusRight',
    [ServiceType.Visa]: 'IconFile',
    [ServiceType.Rebooking]: 'IconPlaneEarth',
    [ServiceType.Babycot]: 'IconBaby',
    [ServiceType.MealplanDP]: 'IconCutlery',
    [ServiceType.Mealplan]: 'IconCutlery',
    [ServiceType.BookingRedeem]: 'IconOrder',
    [ServiceType.Luggage]: 'IconLuggage',
    [ServiceType.SkiPass]: 'IconSkiPass',
    [ServiceType.SkiMaterial]: 'IconSkiMaterial',
    [ServiceType.SkiLesson]: 'IconSkier',
    [ServiceType.TShirt]: 'IconTShirt',
    [ServiceType.Sweater]: 'IconSweater',
    [ServiceType.DutchweekValThorens]: 'IconTicketGeneral',
    [ServiceType.Dutchweek]: 'IconTicketFlight',
    [ServiceType.Comedy]: 'IconTicketGeneral',
    [ServiceType.ParkingAirport]: 'IconParking',
    [ServiceType.SkiTransfer]: 'IconBusRight',
    [ServiceType.SkiCarriage]: 'IconSuitcase',
    [ServiceType.PartyTickets]: 'IconTicketFlight',
    [ServiceType.Golf]: 'IconGolfClub',
    [ServiceType.Bridge]: 'IconTicketGeneral',
    [ServiceType.DinnerVouchers]: 'IconCutlery',
    [ServiceType.GenericServicePerParticipant]: 'IconTicketGeneral',
  };

  return serviceIconMap[service];
};

export const getIconForService = (service: string): IconGlyph | undefined => {
  const serviceIconMap: Record<string, IconGlyph> = {
    participantsBadge: 'Family',
    group: 'Family',
    cutlery: 'ForkAndKnive',
    airplane: 'AirplaneToTop',
    suitcase: 'SuitcaseOnWheels',
    skimaterial: 'Skis',
    skipass: 'SkiPass',
    skilesson: 'PersonSkiing',
    flight: 'AirplaneToTop',
    accommodation: 'Bed',
    building: 'AppartmentBuilding',
    [ServiceType.ESim]: 'Esim',
    [ServiceType.BikeRental]: 'Bike',
    [ServiceType.CarRental]: 'CarRight',
    [ServiceType.CarRentalFiltering]: 'CarRight',
    [ServiceType.TravelInsurance]: 'SuitcaseOnWheelsWithCheckMark',
    [ServiceType.TravelInsurancePerBooking]: 'SuitcaseOnWheelsWithCheckMark',
    [ServiceType.CancellationInsurance]: 'ShieldWithDismiss',
    [ServiceType.Transfer]: 'BusRight',
    [ServiceType.Visa]: 'DocumentWithText',
    [ServiceType.Rebooking]: 'ArrowsSquarePath',
    [ServiceType.Babycot]: 'Baby',
    [ServiceType.MealplanDP]: 'ForkAndKnive',
    [ServiceType.Mealplan]: 'ForkAndKnive',
    [ServiceType.MealplanExternal]: 'ForkAndKnive',
    [ServiceType.BookingRedeem]: 'ArrowsCirclePath',
    [ServiceType.Luggage]: 'SuitcaseOnWheels',
    [ServiceType.SkiPass]: 'SkiPass',
    [ServiceType.SkiMaterial]: 'Skis',
    [ServiceType.SkiLesson]: 'PersonSkiing',
    [ServiceType.TShirt]: 'TShirt',
    [ServiceType.Sweater]: 'Sweater',
    [ServiceType.DutchweekValThorens]: 'Ticket',
    [ServiceType.Dutchweek]: 'TicketFlight',
    [ServiceType.Comedy]: 'Ticket',
    [ServiceType.ParkingAirport]: 'ParkingSign',
    [ServiceType.SkiTransfer]: 'BusRight',
    [ServiceType.SkiCarriage]: 'SuitcaseOnWheels',
    [ServiceType.PartyTickets]: 'Ticket',
    [ServiceType.Golf]: 'GolfClub',
    [ServiceType.Bridge]: 'SpadeSymbol',
    [ServiceType.DinnerVouchers]: 'ForkAndKnive',
    [ServiceType.BreakfastArrivalBus]: 'ForkAndKnive',
    [ServiceType.Breakfast]: 'ForkAndKnive',
    [ServiceType.Bus]: 'BusRight',
    [ServiceType.Dinner]: 'ForkAndKnive',
  };

  return serviceIconMap[service];
};

export const getIconForGenericService = (aName: string): IconGlyph | undefined => {
  const serviceIconMap: Record<string, IconGlyph> = {
    'AUTOHUUR*': 'CarRight',
    'TRAVEL*': 'SuitcaseOnWheelsWithCheckMark',
    'CANCEL*': 'ShieldWithDismiss',
    TRANSFER: 'BusRight',
    'VISA*': 'DocumentWithText',
    REBOOKING: 'ArrowsSquarePath',
    'BABY*': 'Baby',
    ACCOMMODATION: 'ForkAndKnive',
    'VERZORGING*': 'ForkAndKnive',
    'HALFBOARD*': 'ForkAndKnive',
    BREAKFAST: 'ForkAndKnive',
    BREAKFARRHINT_00: 'ForkAndKnive',
    DINNER: 'ForkAndKnive',
    REDEEM: 'ArrowsCirclePath',
    'LUG_*': 'SuitcaseOnWheels',
    'SKI*': 'SkiPass',
    'SKIPAS*': 'SkiPass',
    'SKIHUUR*': 'Skis',
    'SKILES*': 'PersonSkiing',
    'SWEATER*': 'Sweater',
    'TSHIRT*': 'TShirt',
    DUTCHWEEK1: 'TicketFlight',
    DUTCHWEEK: 'TicketFlight',
    COMEDY: 'Ticket',
    PARKEREN: 'ParkingSign',
    SHAREDBUS: 'BusRight',
    'CARRIAGESKI*': 'SuitcaseOnWheels',
    PARTY: 'TicketFlight',
    BUS: 'BusRight',
    GREENFEE: 'GolfClub',
    BRIDGE: 'Ticket',
    'MEAL-VOUCHERS': 'ForkAndKnive',
  };

  return serviceIconMap[aName];
};
